import React, { useEffect, useMemo } from "react";
import { Formik, useFormikContext } from "formik";
import { Switch } from "@headlessui/react";
import _ from "lodash";
import AdmissionSpecialitySearchNSelect from "./AdmissionSpecialitySearchNSelect";
import { useTranslation } from "react-i18next";
import CustomFilterInput from "../../../../shared/CustomFilterInput";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function EncounterForm({ onChange, refId }) {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormikContext();
  const { t, i18n } = useTranslation();
  const currentDate = new Date().toISOString().split("T")[0];
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + 1);
  const defaultEndDate = nextDate.toISOString().split("T")[0];

  const status = [
    { id: "planned", name: t("Planned") },
    { id: "arrived", name: t("Arrived") },
    { id: "triaged", name: t("Triaged") },
    { id: "in-progress", name: t("In Progress") },
    { id: "onleave", name: t("On Leave") },
  ];

  // Dynamic encounter classes based on claimType and subType
  const classes = useMemo(() => {
    const baseClasses = [
      { id: "AMB", name: t("Ambulatory") },
      { id: "EMER", name: t("Emergency") },
      { id: "HH", name: t("Home Health") },
      { id: "IMP", name: t("Inpatient Encounter") },
      { id: "SS", name: t("Short Stay") },
      { id: "FLD", name: t("Field Care") },
      { id: "OBSENC", name: t("Observation") },
      { id: "PREV", name: t("Pre-admission") },
      { id: "VR", name: t("Virtual") },
      { id: "OTH", name: t("Other Encounters") },
    ];

    switch (values.claimType) {
      case "institutional":
        return [
          { id: "IMP", name: t("Inpatient Encounter") },
          { id: "SS", name: t("Short Stay") },
        ];
      case "professional":
        return values.subType === "op"
          ? [
              { id: "VR", name: t("Virtual") },
              { id: "AMB", name: t("Ambulatory") },
              { id: "OTH", name: t("Other Encounters") },
            ]
          : [{ id: "EMER", name: t("Emergency") }];

      case "oral":
        return [{ id: "AMB", name: t("Ambulatory") }];

      default:
        return baseClasses;
    }
  }, [values.claimType, values.subType]);

  const defaultClass = "AMB";

  const serviceEventType = [
    { id: "ICSE", name: t("Initial client service event") },
    { id: "SCSE", name: t("Subsequent client service event") },
  ];

  const defaultServiceEvent = "ICSE";

  const serviceTypes = [
    { id: "acute-care", name: t("Acute Care") },
    { id: "sub-acute-care", name: t("Sub-Acute Care") },
    { id: "rehabilitation", name: t("Rehabilitation") },
    { id: "mental-behavioral", name: t("Mental & Behavioral") },
    { id: "geriatric-care", name: t("Geriatric Care") },
  ];

  const defaultServiceType = "acute-care";

  const adminSources = [
    { id: "IA", name: t("IA - Immediate Admission") },
    { id: "EER", name: t("EER - Admission from hospital ER") },
    {
      id: "EOP",
      name: t("EOP - Emergency Admission from hospital outpatient"),
    },
    {
      id: "EGPHC",
      name: t(
        "EGPHC - Emergency Admission by referral from government primary healthcare center",
      ),
    },
    {
      id: "EGGH",
      name: t(
        "EGGH - Emergency Admission by referral from general government hospital",
      ),
    },
    {
      id: "EPPHC",
      name: t(
        "EPPHC - Emergency Admission by referral from private primary healthcare center",
      ),
    },
    {
      id: "EPH",
      name: t("EPH - Emergency Admission by referral from private hospital"),
    },
    { id: "EIC", name: t("EIC - Emergency Admission by insurance company") },
    {
      id: "EWGS",
      name: t("EWGS - Elective waiting list admission government free Scheme"),
    },
    {
      id: "EWSS",
      name: t("EWSS - Elective waiting list admission self-payment Scheme"),
    },
    {
      id: "EWIS",
      name: t(
        "EWIS - Elective waiting list admission insurance coverage Scheme",
      ),
    },
    { id: "EMBA", name: t("EMBA - Emergency Maternity Birth Admission") },
    { id: "PMBA", name: t("PMBA - Planned Maternity Birth Admission") },
    { id: "Others", name: t("Others") },
  ];

  const cnhiAdminSources = [
    { id: "RECR", name: t("RECR - Red crescent") },
    { id: "WKIN", name: t("WKIN - Walk-in") },
    { id: "FMIYM", name: t("FMIYM - Family member") },
    { id: "AA", name: t("AA - Already admitted") },
    { id: "PVAMB", name: t("PVAMB - Private ambulance") },
    {
      id: "AAIC",
      name: t("AAIC - Already admitted- insurance consumed"),
    },
  ];

  const isCnhiPayer = values.payerLicenseId === "0000000163";

  const adminSourceOptions = isCnhiPayer
    ? [
        ...adminSources,
        ...cnhiAdminSources.filter(
          (item) => !adminSources.some((source) => source.id === item.id),
        ),
      ]
    : adminSources;

  useEffect(() => {
    if (!values.adminSource) {
      return;
    }

    const allowedAdminSourceIds = new Set(
      adminSourceOptions.map((item) => item.id),
    );

    if (!allowedAdminSourceIds.has(values.adminSource)) {
      setFieldValue("adminSource", isCnhiPayer ? "IA" : null);
    }
  }, [adminSourceOptions, isCnhiPayer, setFieldValue, values.adminSource]);


  const dischargeDispositions = [
    {
      id: "acute-hospital",
      name: t("Discharge/transfer to an Acute Hospital"),
    },
    { id: "SDTC", name: t("Statistical Discharge-Type Change") },
    { id: "SDFL", name: t("Statistical discharge from leave") },
    { id: "home", name: t("Home/Other") },
    {
      id: "DTRAS",
      name: t("Discharge /Transfer to a Residential Ageing Service"),
    },
    { id: "DTPH", name: t("Discharge/Transfer to a Psychiatric Hospital") },
    {
      id: "DTOHA",
      name: t("Discharge /Transfer to Other Health Care Accommodation"),
    },
    { id: "LAMA", name: t("Left Against Medical Advice") },
    { id: "died", name: t("Died") },
    { id: "in-hospital", name: t("Patient still in hospital") },
  ];

  const emergencyArivalCode = [
    { id: "GEMSA", name: t("Ground EMS Ambulance") },
    { id: "MOHA", name: t("Ground MOH Ambulance") },
    { id: "GCDA", name: t("Ground Civil Defense Ambulance") },
    { id: "GMA", name: t("Ground Military Ambulance") },
    { id: "GPA", name: t("Ground Private Ambulance") },
    { id: "EMSAA", name: t("EMS Air Ambulance") },
    { id: "ACDA", name: t("Air Civil Défense Ambulance") },
    { id: "AMA", name: t("Air Military Ambulance") },
    { id: "PV", name: t("Police Vehicle") },
    { id: "OGV", name: t("Other Government Vehicles") },
    { id: "PV", name: t("Personal Vehicle") },
    { id: "other", name: t("Other") },
    { id: "unknown", name: t("Not stated/unknown") },
  ];

  const emergencyDepartmentDisposition = [
    { id: "AH", name: t("Admitted to this hospital") },
    {
      id: "NAD",
      name: t("Non-admitted patient ED episode completed-departed"),
    },
    {
      id: "NAR",
      name: t("Non-admitted patient ED episode completed-referred"),
    },
    { id: "DNW", name: t("Did Not Wait") },
    { id: "LAOR", name: t("Left at Own Risk") },
    { id: "DED", name: t("Died in ED") },
    { id: "DOA", name: t("Dead on Arrival") },
    { id: "R", name: t("Registered") },
  ];

  const triageCategory = [
    { id: "IR", name: t("Immediate resuscitation") },
    { id: "VU", name: t("Very Urgent") },
    { id: "U", name: t("Urgent") },
    { id: "SER", name: t("Standard ER") },
    { id: "NU", name: t("Non-Urgent") },
  ];

  const intendedLengthOfStays = [
    { id: "ISD", name: t("Intended same day") },
    { id: "IO", name: t("Intended overnight") },
  ];

  const reAdmission = [{ id: "R", name: t("Re-Admission") }];

  const priorities = [
    { id: "EL", name: t("Elective") },
    { id: "EM", name: t("Emergency") },
  ];

  useEffect(() => {
    if (
      values.claimType === "institutional" ||
      values.claimType === "professional" ||
      values.claimType === "oral"
    ) {
      setFieldValue("isEncounter", true);
    } else {
      setFieldValue("isEncounter", false);
    }
  }, [values.claimType, setFieldValue]);

  useEffect(() => {
    if (values.claimType === "professional" || values.claimType === "oral") {
      setFieldValue(
        "encounterClass",
        values?.encounterClass ? values?.encounterClass : "AMB",
      );
      setFieldValue(
        "serviceEventType",
        values?.serviceEventType ? values?.serviceEventType : "ICSE",
      );
    } else {
      setFieldValue(
        "encounterClass",
        values?.encounterClass ? values?.encounterClass : null,
      );
      setFieldValue(
        "serviceEventType",
        values?.serviceEventType ? values?.serviceEventType : null,
      );
    }
  }, [
    values.claimType,
    values.encounterClass,
    values.serviceEventType,
    setFieldValue,
  ]);

  return (
    <div className="personal-data !p-2 !mb-2">
      <div className="personal-title !mb-2">
        <div className="inventory-svg">
          <svg
            width="26"
            height="26"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="34" height="34" rx="8" fill="#F4F6F8"></rect>
            <path
              d="M9.25 11H11.25V14H21.25V11H23.25V16H25.25V11C25.25 9.9 24.35 9 23.25 9H19.07C18.65 7.84 17.55 7 16.25 7C14.95 7 13.85 7.84 13.43 9H9.25C8.15 9 7.25 9.9 7.25 11V25C7.25 26.1 8.15 27 9.25 27H15.25V25H9.25V11ZM16.25 9C16.8 9 17.25 9.45 17.25 10C17.25 10.55 16.8 11 16.25 11C15.7 11 15.25 10.55 15.25 10C15.25 9.45 15.7 9 16.25 9Z"
              fill="#0070c2"
            ></path>
            <path
              d="M25.25 17.5L19.76 23L16.75 20L15.25 21.5L19.76 26L26.75 19L25.25 17.5Z"
              fill="#0070c2"
            ></path>
          </svg>
        </div>
        <div className="w-full flex items-center justify-between">
          <span className="font-semibold text-gray-800">{t("Encounter")}</span>
          <div className="flex space-x-3 md:mt-0 md:ml-4 mr-2">
            <Switch
              checked={values.isEncounter ?? true} // Default to true if not already set
              onChange={() => setFieldValue("isEncounter", !values.isEncounter)}
              className="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-[#0070c2] focus:ring-offset-2"
            >
              <span className="sr-only">{t("Use setting")}</span>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute h-full w-full rounded-md bg-white"
              />
              <span
                aria-hidden="true"
                className={classNames(
                  values.isEncounter ? "!bg-[#0070c2]" : "bg-gray-200",
                  "pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out",
                )}
              />
              <span
                aria-hidden="true"
                className={classNames(
                  values.isEncounter ? "translate-x-6" : "translate-x-0",
                  "pointer-events-none absolute left-0 inline-block h-4 w-4 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out",
                )}
              />
            </Switch>
          </div>
        </div>
      </div>
      {values.isEncounter && (
        <>
          {values.claimType === "institutional" ||
          values.claimType === "professional" ||
          values.claimType === "oral" ||
          values.claimType === "vission" ? (
            <>
              <div className="encounter-fields hidden">
                <span>{t("Encounter")}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-2">
                <CustomFilterInput
                  label={t("Status*")}
                  name="encounterStatus"
                  isSelect
                  options={status}
                  value={values.encounterStatus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.encounterStatus}
                  touched={touched.encounterStatus}
                  placeholder={t("Select Status")}
                />

                <CustomFilterInput
                  label={t("Class*")}
                  name="encounterClass"
                  isSelect
                  options={classes}
                  value={values.encounterClass}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.encounterClass}
                  touched={touched.encounterClass}
                  placeholder={t("Select Class")}
                />

                <CustomFilterInput
                  label={t("Service Type")}
                  name="serviceType"
                  isSelect
                  options={serviceTypes}
                  value={values.serviceType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={t("Select Service Type")}
                />

                {(values.claimType === "professional" ||
                  values.claimType === "oral") && (
                  <CustomFilterInput
                    label={t("Service Event Type*")}
                    name="serviceEventType"
                    isSelect
                    options={serviceEventType}
                    value={values.serviceEventType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.serviceEventType}
                    touched={touched.serviceEventType}
                    placeholder={t("Select Event Type")}
                  />
                )}

                <CustomFilterInput
                  label={t("Priority")}
                  name="encounterPriority"
                  isSelect={true}
                  options={priorities}
                  value={values.encounterPriority}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={t("Priority")}
                />

                <CustomFilterInput
                  label={t("Period Start*")}
                  name="periodStart"
                  type="date"
                  value={values.periodStart}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.periodStart}
                  touched={touched.periodStart}
                  max={
                    values.periodEnd && values.periodEnd < currentDate
                      ? values.periodEnd
                      : currentDate
                  }
                />

                <CustomFilterInput
                  label={
                    values.encounterClass === "IMP"
                      ? t("Period End*")
                      : t("Period End")
                  }
                  name="periodEnd"
                  type="date"
                  value={values.periodEnd}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.periodEnd}
                  touched={touched.periodEnd}
                  max={currentDate}
                  min={values.periodStart || undefined}
                />
              </div>
            </>
          ) : null}

          {values.claimType === "professional" && values.subType === "emr" ? (
            <>
              <div className="encounter-fields hidden">
                <span>{t("Encounter Emergency")}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-2">
                <CustomFilterInput
                  label={t("Emergency Arrival Code*")}
                  name="emergencyArivalCode"
                  isSelect
                  options={emergencyArivalCode}
                  value={values.emergencyArivalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.emergencyArivalCode}
                  touched={touched.emergencyArivalCode}
                  placeholder={t("Select Arrival Code")}
                />

                <CustomFilterInput
                  label={t("Emergency Service Date*")}
                  name="emergencyServiceDate"
                  type="date"
                  value={values.emergencyServiceDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.emergencyServiceDate}
                  touched={touched.emergencyServiceDate}
                />

                <CustomFilterInput
                  label={t("Emergency Department Disposition")}
                  name="emergencyDepartmentDisposition"
                  isSelect={true}
                  options={emergencyDepartmentDisposition}
                  value={values.emergencyDepartmentDisposition}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.emergencyDepartmentDisposition}
                  touched={touched.emergencyDepartmentDisposition}
                  placeholder={t("Emergency Department Disposition")}
                />

                <CustomFilterInput
                  label={t("Triage Category*")}
                  name="triageCategory"
                  isSelect
                  options={triageCategory}
                  value={values.triageCategory}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.triageCategory}
                  touched={touched.triageCategory}
                  placeholder={t("Select Category")}
                />

                <CustomFilterInput
                  label={t("Triage Date*")}
                  name="triageDate"
                  type="date"
                  value={values.triageDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.triageDate}
                  touched={touched.triageDate}
                />
              </div>
            </>
          ) : null}
          {values.claimType === "institutional" ? (
            <>
              <div className="encounter-fields hidden">
                <span>{t("Hospitalization")}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-2">
                <CustomFilterInput
                  label={t("Admin Source*")}
                  name="adminSource"
                  isSelect
                  options={adminSourceOptions}
                  value={values.adminSource}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.adminSource}
                  touched={touched.adminSource}
                  placeholder={t("Select Source")}
                />

                <CustomFilterInput
                  label={t("Re-Admission")}
                  name="readmission"
                  isSelect
                  options={reAdmission}
                  value={values.readmission}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={t("Select Option")}
                />

                <CustomFilterInput
                  label={t("Discharge Disposition")}
                  name="dischargeDisposition"
                  isSelect={true}
                  options={dischargeDispositions}
                  value={values.dischargeDisposition}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.dischargeDisposition}
                  touched={touched.dischargeDisposition}
                  placeholder={t("Discharge Disposition")}
                />

                <div className="flex flex-col gap-0.5 w-full relative">
                  <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
                    {t("Admission Speciality*")}{" "}
                    {values.admissionSpeciality &&
                      `(${values.admissionSpeciality})`}
                  </label>
                  <AdmissionSpecialitySearchNSelect
                    setFieldValue={(val) =>
                      setFieldValue(`admissionSpeciality`, val.code)
                    }
                    error={Boolean(
                      touched.admissionSpeciality && errors.admissionSpeciality
                    )}
                  />
                  {touched.admissionSpeciality &&
                    errors.admissionSpeciality && (
                      <p className="text-[10px] text-red-500 mt-1 font-medium">
                        {errors.admissionSpeciality}
                      </p>
                    )}
                </div>
                <div className="flex flex-col gap-0.5 w-full relative">
                  <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
                    {t("Discharge Speciality*")}{" "}
                    {values.dischargeSpeciality &&
                      `(${values.dischargeSpeciality})`}
                  </label>
                  <AdmissionSpecialitySearchNSelect
                    setFieldValue={(val) =>
                      setFieldValue(`dischargeSpeciality`, val.code)
                    }
                    error={Boolean(
                      touched.dischargeSpeciality && errors.dischargeSpeciality
                    )}
                  />
                  {touched.dischargeSpeciality &&
                    errors.dischargeSpeciality && (
                      <p className="text-[10px] text-red-500 mt-1 font-medium">
                        {errors.dischargeSpeciality}
                      </p>
                    )}
                </div>

                <CustomFilterInput
                  label={t("Intended Length Of Stay*")}
                  name="intendedLengthOfStay"
                  isSelect
                  options={intendedLengthOfStays}
                  value={values.intendedLengthOfStay}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.intendedLengthOfStay}
                  touched={touched.intendedLengthOfStay}
                  placeholder={t("Select Stay")}
                />
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default EncounterForm;
